export const STAT_CATEGORIES = [
    { id: 'city', label: 'Urzędy miast', group: 'administration' },
    { id: 'commune', label: 'Urzędy gmin', group: 'administration' },
    { id: 'county', label: 'Starostwa powiatowe', group: 'administration' },
    { id: 'court', label: 'Sądy', group: 'administration' },
    { id: 'administration', label: 'Inne instytucje publiczne', group: 'administration' },
    { id: 'library', label: 'Biblioteki', group: 'culture' },
    { id: 'theater', label: 'Teatry', group: 'culture' },
    { id: 'museum', label: 'Muzea', group: 'culture' },
    { id: 'culture', label: 'Ośrodki kultury', group: 'culture' },
    { id: 'cinema', label: 'Kina', group: 'culture' },
    { id: 'academy', label: 'Akademie', group: 'education' },
    { id: 'university', label: 'Uniwersytety', group: 'education' },
    { id: 'polytechnic', label: 'Politechniki', group: 'education' },
    { id: 'higherSchool', label: 'Szkoły wyższe', group: 'education' },
    { id: 'school', label: 'Szkoły i zespoły szkolne', group: 'education' },
    { id: 'zusBranch', label: 'Oddziały', group: 'zus' },
    { id: 'zusInspectorate', label: 'Inspektoraty', group: 'zus' },
    { id: 'zusOffice', label: 'Biura terenowe', group: 'zus' },
    { id: 'zusOther', label: 'Inne placówki ZUS', group: 'zus' },
    { id: 'other', label: 'Poza kategoriami', group: 'other' },
    { id: 'review', label: 'Do weryfikacji', group: 'other' },
] as const;

export type StatCategoryId = typeof STAT_CATEGORIES[number]['id'];
export type StatGroupId = typeof STAT_CATEGORIES[number]['group'];

export const CATEGORY_RULES = {
    higherEducation: [
        { category: 'university', names: [/\buniwersytet(?:u)?\b/] },
        { category: 'academy', names: [/\bakademia\b/] },
        { category: 'polytechnic', names: [/\bpolitechnik[ai]\b/] },
        {
            category: 'higherSchool',
            names: [
                /\bwyzsza szkola\b/, /\bszkola wyzsza\b/, /\bszkoly wyzszej\b/,
                /\bszkola glowna\b/, /\buczelni[ae]\b/, /\bcollegium\b/,
            ],
        },
    ],
    administration: [
        {
            category: 'city',
            names: [
                /urz(?:ad|edu) miasta\b/,
                /urz(?:ad|edu) miejski(?:ego)?\b/,
                /urz(?:ad|edu) gminy i miasta\b/,
                /^ratusz\b/,
            ],
        },
        { category: 'commune', names: [/urz(?:ad|edu) gminy\b/] },
        { category: 'county', names: [/starostw(?:o|a) powiatow(?:e|ego)\b/] },
        { category: 'court', names: [/\bsad (?:rejonowy|okregowy|apelacyjny|administracyjny)\b/] },
        {
            category: 'administration',
            names: [
                /\burzad (?:wojewodzki|marszalkowski|pracy|skarbowy)\b/,
                /\burzad (?:stanu cywilnego|ochrony zabytkow|gorniczy|lotnictwa)\b/,
                /osrodek pomocy spolecznej/, /osrodek pomocy rodzinie/, /centrum pomocy rodzinie/,
                /centrum uslug spolecznych/, /centrum swiadczen/, /straz miejska/,
                /\b(?:mops|gops|mopr|pcpr|cus|ops)\b/,
            ],
        },
    ],
    culture: [
        { category: 'library', names: [/\bbibliotek[a-z]*\b/, /\bksiaznica\b/, /\bmediateka\b/] },
        { category: 'cinema', names: [/\bkino\b/] },
        { category: 'theater', names: [/\bteatr(?:u)?\b/] },
        { category: 'museum', names: [/\bmuzeum\b/] },
        { category: 'culture', names: [/\b(?:dom|osrodek|centrum|instytut|strefa|pawilon|forum) kultury\b/] },
    ],
    zus: [
        { category: 'zusInspectorate', names: [/inspektorat/], institutionStart: /inspektorat/iu },
        { category: 'zusOffice', names: [/biuro terenowe/], institutionStart: /biuro terenowe/iu },
        {
            category: 'zusBranch', names: [/oddzial/],
            institutionStart: /(?:(?:[IVX]+|Pierwszy|Drugi|Trzeci)\s+)?oddział/iu,
        },
    ],
} as const;

export const NAME_PATTERNS = {
    excluded: [/^(?:przystanek|przejscie)\b/, /^rybnik\b.*\bprzystanek\b/],
    zus: [/\bzus\b/, /zaklad(?:u)? ubezpieczen spolecznych/],
    incompleteEducation: [/^wydzial/, /^akademickie centrum/, /^szkola w\b/],
    county: [/starostw[a-z]* powiatow[a-z]*/],
    conflictingCounty: [/\burz(?:ad|edu)\b/, /^szpital\b/],
    ambiguousPublicInstitution: [/\bprokuratura\b/, /\bsad okregowy i sady rejonowe\b/],
    ambiguousAdministration: [/\bi (?:gminny|osrodek)\b/],
    museumBranch: [/oddzial muzeum/],
    school: [
        /szkola podstawowa/, /\bliceum\b/, /\btechnikum\b/, /zespol szkol/,
        /osrodek szkolno[ -]wychowawczy/,
    ],
    otherRequiringReview: [
        /przedszkol/, /zlobek/, /instytut teatralny/,
        /galeria (?:sztuki|miejska|artystyczna)/, /planetarium/, /^ksieznica.*biblioteka/,
    ],
} as const;

export const GENERIC_INSTITUTION_NAMES = {
    higherEducation: ['uniwersytet', 'politechnika', 'akademia', 'szkola wyzsza', 'collegium'],
    administration: [
        'urzad miasta', 'urzad miejski', 'urzad gminy', 'urzad stanu cywilnego', 'urzad skarbowy',
        'starostwo powiatowe', 'ratusz', 'miejski osrodek pomocy spolecznej', 'osrodek pomocy spolecznej',
        'powiatowe centrum pomocy rodzinie', 'straz miejska', 'centrum uslug spolecznych',
        'miejski osrodek pomocy rodzinie', 'wojewodzki sad administracyjny', 'powiatowy urzad pracy', 'mops',
    ],
    library: ['biblioteka publiczna', 'miejska biblioteka publiczna', 'powiatowa biblioteka publiczna', 'mediateka'],
};

export const INSTITUTION_UNIT_PATTERNS = {
    higherEducation: [
        /wydzia[lł](?:y|u)?/, /budynek/, /budynki/, /kampus/, /biblioteka/, /dom studenta/,
        /dom studencki/, /akademik/, /centrum/, /katedra/, /instytut/, /inspektorat/, /sto[lł][oó]wka/,
        /biuro/, /studium/, /szko[lł]a zdrowia/, /liceum/, /akademickie/, /studenckie/, /collegium/,
        /kolegium/, /pawilon/, /p[lł]ywalnia/, /rektorat/, /filia/, /sala/, /audytorium/, /aula/,
        /hotel/, /hala/, /przestrze[nń]/, /siedziba/, /biocentrum/, /gmach/, /pa[lł]ac/, /oficyna/,
        /szara willa/, /buw/, /stara kot[lł]ownia/, /kortosfera/, /stra[zż] uniwersytecka/,
        /uniwersytecki/, /uniwersyteckie/, /ogr[oó]d/, /poliklinika/, /szklarnie/, /zesp[oó][lł] obiekt[oó]w/,
        /obiekty/, /szatnia/, /klinika/, /dzia[lł]/, /willa/, /szpital/, /romanistyka/, /klub politechnik/,
    ],
    administration: [
        /(?:[ivx]+ )?wydzia[lł]/, /biuro/, /urz[aą]d stanu cywilnego/, /punkt obs[lł]ugi/,
        /terenowy punkt/, /delegatura/, /referat/, /wej[sś]cie/, /centrum organizacji/,
        /miejski zesp[oó][lł]/, /stra[zż] miejska/, /centrum us[lł]ug/, /schronisko/, /ratusz/,
        /villa/, /trybuna[lł]/, /filia/, /powiatowy o[sś]rodek/, /dzia[lł]/,
        /zesp[oó][lł] terenowej/, /dzienny dom/, /pensjonat/,
    ],
    zus: [/wej[sś]cie/, /sala/, /pi[eę]tro/, /lekarze/, /orzecznictwo/],
    library: [/filia/, /oddzia[lł]/, /wypo[zż]yczalnia/, /czytelnia/, /dyrekcja/, /mediateka/, /gmach/, /chotomek/, /wej[sś]cie/],
    cinema: [/wej[sś]cie/, /kasa/, /sala/],
    theater: [/wej[sś]cie/, /kasa/, /scena/],
    museum: [/wej[sś]cie/, /kasa/, /budynek/],
    culture: [
        /filia/, /sala/, /galeria/, /pracownia/, /miejsce aktywno[sś]ci/, /fort/, /prochownia/,
        /centrum lokalne/, /ango[bp]a/, /magazyn sztuk/, /zielone oko/,
    ],
    school: [/budynek/, /wej[sś]cie/],
} as const;

export const NAME_CORRECTIONS = [
    { pattern: /\bUniwersyter\b/giu, replacement: 'Uniwersytet' },
    { pattern: /([a-ząęćłńóśźż])(Uniwersytet)/gu, replacement: '$1 $2' },
    { pattern: /^UMiG\b/iu, replacement: 'Urząd Miasta i Gminy' },
];
